import { zonedWallTimeToIso } from '@ly360/i18n';
import { DateField, DateTimeField, Dialog, ErrorState, Select, useFormat } from '@ly360/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { CRM_KEYS, useAccountOptions, useDirectory, usePeopleOptions, usePipelines } from './data';
import { AccountPicker, PersonPicker, SimilarCompaniesHint, SimilarPeopleHint } from './pickers';

export function Field({
  id,
  label,
  children,
  hint,
}: {
  id: string;
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

function useCrmMutation<T>(fn: (body: T) => Promise<{ id: string }>, onDone: (id: string) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      for (const k of CRM_KEYS) void qc.invalidateQueries({ queryKey: [k] });
      onDone(r.id);
    },
  });
}

const post = (path: string) => (body: unknown) =>
  api<{ id: string }>(path, { method: 'POST', body: JSON.stringify(body) });

function Footer({ onClose, busy, label }: { onClose: () => void; busy: boolean; label: string }) {
  const { t } = useTranslation();
  return (
    <>
      <button type="button" className="btn btn-ghost" onClick={onClose}>
        {t('common.cancel')}
      </button>
      <button type="submit" className="btn btn-grad" disabled={busy}>
        {busy ? t('common.saving') : label}
      </button>
    </>
  );
}

export function UserSelect({
  id,
  value,
  onChange,
  triggerClassName,
  'aria-label': ariaLabel,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  triggerClassName?: string;
  'aria-label'?: string;
}) {
  const { t, i18n } = useTranslation();
  const dir = useDirectory();
  const ar = i18n.language === 'ar';
  return (
    <Select
      id={id}
      value={value}
      onChange={onChange}
      searchable
      triggerClassName={triggerClassName}
      aria-label={ariaLabel}
      placeholder={t('common.nobody')}
      options={[
        { value: '', label: t('common.nobody') },
        ...(dir.data?.items ?? []).map((u) => ({
          value: u.id,
          label: ar && u.displayNameAr ? u.displayNameAr : u.displayName,
        })),
      ]}
    />
  );
}

export function PersonSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  const q = usePeopleOptions();
  return (
    <Select
      id={id}
      value={value}
      onChange={onChange}
      searchable
      placeholder={t('common.none')}
      options={[
        { value: '', label: t('common.none') },
        ...(q.data?.items ?? []).map((p) => ({ value: p.id, label: p.displayName })),
      ]}
    />
  );
}

export function AccountSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const q = useAccountOptions();
  return (
    <Select
      id={id}
      value={value}
      onChange={onChange}
      searchable
      placeholder={t('common.none')}
      options={[
        { value: '', label: t('common.none') },
        ...(q.data?.items ?? []).map((a) => ({
          value: a.id,
          label: i18n.language === 'ar' && a.nameAr ? a.nameAr : a.name,
        })),
      ]}
    />
  );
}

const nul = (v: string) => (v.trim() ? v.trim() : undefined);

export function NewPersonDialog({
  open,
  onClose,
  accountId,
  initialName,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  accountId?: string;
  /** Pre-fills the name (e.g. typed in a person picker). */
  initialName?: string;
  /** Instead of opening the new person's page, hand the id back (creating from inside another form). */
  onCreated?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [f, setF] = useState({
    displayName: initialName ?? '',
    nameArabic: '',
    jobTitle: '',
    phone: '',
    email: '',
    accountId: accountId ?? '',
    role: '',
    lifecycleStage: 'contact',
  });
  const m = useCrmMutation(post('/v1/people'), (id) => {
    if (onCreated) return onCreated(id);
    onClose();
    navigate(`/people/${id}`);
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF({ ...f, [k]: e.target.value });
  return (
    <Dialog
      open={open}
      title={t('crm.people.new')}
      onClose={onClose}
      testId="new-person"
      onSubmit={() =>
        m.mutate({
          displayName: f.displayName,
          nameArabic: nul(f.nameArabic),
          jobTitle: nul(f.jobTitle),
          phones: f.phone.trim() ? [f.phone.trim()] : [],
          emails: f.email.trim() ? [f.email.trim()] : [],
          accountId: nul(f.accountId),
          role: nul(f.role),
          lifecycleStage: f.lifecycleStage,
        })
      }
      footer={<Footer onClose={onClose} busy={m.isPending} label={t('common.create')} />}
    >
      <SimilarPeopleHint name={f.displayName} phone={f.phone} email={f.email} onUse={onCreated} />
      <div className="form-grid">
        <Field id="np-name" label={t('crm.people.field.displayName')}>
          <input
            id="np-name"
            className="input"
            required
            value={f.displayName}
            onChange={set('displayName')}
          />
        </Field>
        <Field id="np-name-ar" label={t('crm.people.field.nameArabic')}>
          <input
            id="np-name-ar"
            className="input"
            dir="rtl"
            lang="ar"
            value={f.nameArabic}
            onChange={set('nameArabic')}
          />
        </Field>
        <Field id="np-phone" label={t('crm.people.field.phone')}>
          <input
            id="np-phone"
            className="input"
            dir="ltr"
            type="tel"
            value={f.phone}
            onChange={set('phone')}
            placeholder="+971 50 123 4567"
          />
        </Field>
        <Field id="np-email" label={t('crm.people.field.email')}>
          <input
            id="np-email"
            className="input"
            dir="ltr"
            type="email"
            value={f.email}
            onChange={set('email')}
          />
        </Field>
        <Field id="np-title" label={t('crm.people.field.jobTitle')}>
          <input id="np-title" className="input" value={f.jobTitle} onChange={set('jobTitle')} />
        </Field>
        <Field id="np-stage" label={t('crm.people.field.stage')}>
          <Select
            id="np-stage"
            value={f.lifecycleStage}
            onChange={(v) => setF({ ...f, lifecycleStage: v })}
            options={(['lead', 'contact', 'customer', 'churned'] as const).map((s) => ({
              value: s,
              label: t(`crm.lifecycle.${s}`),
            }))}
          />
        </Field>
        <Field id="np-account" label={t('crm.people.field.company')}>
          <AccountPicker id="np-account" value={f.accountId} onChange={(v) => setF({ ...f, accountId: v })} />
        </Field>
        <Field id="np-role" label={t('crm.people.field.role')}>
          <input id="np-role" className="input" value={f.role} onChange={set('role')} />
        </Field>
      </div>
      {m.isError && <ErrorState error={m.error} />}
    </Dialog>
  );
}

export function NewCompanyDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [f, setF] = useState({
    name: '',
    nameAr: '',
    industry: '',
    website: '',
    phone: '',
    email: '',
    city: '',
    vatNumber: '',
  });
  const m = useCrmMutation(post('/v1/accounts'), (id) => {
    onClose();
    navigate(`/companies/${id}`);
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF({ ...f, [k]: e.target.value });
  return (
    <Dialog
      open={open}
      title={t('crm.companies.new')}
      onClose={onClose}
      testId="new-company"
      onSubmit={() => m.mutate(Object.fromEntries(Object.entries(f).map(([k, v]) => [k, nul(v)])))}
      footer={<Footer onClose={onClose} busy={m.isPending} label={t('common.create')} />}
    >
      <SimilarCompaniesHint name={f.name} />
      <div className="form-grid">
        <Field id="nc-name" label={t('crm.companies.field.name')}>
          <input id="nc-name" className="input" required value={f.name} onChange={set('name')} />
        </Field>
        <Field id="nc-name-ar" label={t('crm.companies.field.nameAr')}>
          <input
            id="nc-name-ar"
            className="input"
            dir="rtl"
            lang="ar"
            value={f.nameAr}
            onChange={set('nameAr')}
          />
        </Field>
        <Field id="nc-industry" label={t('crm.companies.field.industry')}>
          <input id="nc-industry" className="input" value={f.industry} onChange={set('industry')} />
        </Field>
        <Field id="nc-city" label={t('crm.companies.field.city')}>
          <input id="nc-city" className="input" value={f.city} onChange={set('city')} />
        </Field>
        <Field id="nc-phone" label={t('crm.companies.field.phone')}>
          <input id="nc-phone" className="input" dir="ltr" value={f.phone} onChange={set('phone')} />
        </Field>
        <Field id="nc-email" label={t('crm.companies.field.email')}>
          <input
            id="nc-email"
            className="input"
            dir="ltr"
            type="email"
            value={f.email}
            onChange={set('email')}
          />
        </Field>
        <Field id="nc-web" label={t('crm.companies.field.website')}>
          <input id="nc-web" className="input" dir="ltr" value={f.website} onChange={set('website')} />
        </Field>
        <Field id="nc-vat" label={t('crm.companies.field.vatNumber')}>
          <input id="nc-vat" className="input" dir="ltr" value={f.vatNumber} onChange={set('vatNumber')} />
        </Field>
      </div>
      {m.isError && <ErrorState error={m.error} />}
    </Dialog>
  );
}

export function NewDealDialog({
  open,
  onClose,
  personId,
  accountId,
  stageId,
}: {
  open: boolean;
  onClose: () => void;
  personId?: string;
  accountId?: string;
  stageId?: string;
}) {
  const { t, i18n } = useTranslation();
  const { session } = useAuth();
  const pipes = usePipelines();
  const [f, setF] = useState({
    title: '',
    amount: '',
    stageId: stageId ?? '',
    personId: personId ?? '',
    accountId: accountId ?? '',
    expectedCloseDate: '',
  });
  const m = useCrmMutation(post('/v1/deals'), () => onClose());
  const stages = pipes.data?.items[0]?.stages.filter((s) => s.kind === 'open') ?? [];
  return (
    <Dialog
      open={open}
      title={t('crm.deals.new')}
      onClose={onClose}
      testId="new-deal"
      onSubmit={() =>
        m.mutate({
          title: f.title,
          amount: Number(f.amount || 0),
          stageId: nul(f.stageId),
          personId: nul(f.personId),
          accountId: nul(f.accountId),
          expectedCloseDate: nul(f.expectedCloseDate),
        })
      }
      footer={<Footer onClose={onClose} busy={m.isPending} label={t('common.create')} />}
    >
      <div className="form-grid">
        <Field id="nd-title" label={t('crm.deals.field.title')}>
          <input
            id="nd-title"
            className="input"
            required
            value={f.title}
            onChange={(e) => setF({ ...f, title: e.target.value })}
          />
        </Field>
        <Field id="nd-amount" label={`${t('crm.deals.field.amount')} (${session?.tenant.currency})`}>
          <input
            id="nd-amount"
            className="input"
            dir="ltr"
            type="number"
            min={0}
            step="0.001"
            inputMode="decimal"
            value={f.amount}
            onChange={(e) => setF({ ...f, amount: e.target.value })}
          />
        </Field>
        <Field id="nd-stage" label={t('crm.deals.field.stage')}>
          <Select
            id="nd-stage"
            value={f.stageId}
            onChange={(v) => setF({ ...f, stageId: v })}
            options={stages.map((s) => ({
              value: s.id,
              label: i18n.language === 'ar' ? s.nameAr : s.nameEn,
            }))}
          />
        </Field>
        <Field id="nd-close" label={t('crm.deals.field.expectedClose')}>
          <DateField
            id="nd-close"
            value={f.expectedCloseDate}
            onChange={(v) => setF({ ...f, expectedCloseDate: v })}
          />
        </Field>
        <Field id="nd-account" label={t('crm.deals.field.company')}>
          <AccountPicker id="nd-account" value={f.accountId} onChange={(v) => setF({ ...f, accountId: v })} />
        </Field>
        <Field id="nd-person" label={t('crm.deals.field.person')}>
          <PersonPicker
            id="nd-person"
            value={f.personId}
            accountId={f.accountId || undefined}
            onChange={(v) => setF({ ...f, personId: v })}
          />
        </Field>
      </div>
      {m.isError && <ErrorState error={m.error} />}
    </Dialog>
  );
}

export function NewCaseDialog({
  open,
  onClose,
  personId,
  accountId,
  conversationId,
}: {
  open: boolean;
  onClose: () => void;
  personId?: string;
  accountId?: string;
  conversationId?: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [f, setF] = useState({
    subject: '',
    description: '',
    priority: 'normal',
    category: '',
    personId: personId ?? '',
    assigneeUserId: '',
    regulatorFlag: false,
  });
  const m = useCrmMutation(post('/v1/cases'), (id) => {
    onClose();
    navigate(`/cases/${id}`);
  });
  return (
    <Dialog
      open={open}
      title={t('crm.cases.new')}
      onClose={onClose}
      testId="new-case"
      onSubmit={() =>
        m.mutate({
          subject: f.subject,
          description: nul(f.description),
          priority: f.priority,
          category: nul(f.category),
          personId: nul(f.personId),
          accountId,
          conversationId,
          assigneeUserId: nul(f.assigneeUserId),
          regulatorFlag: f.regulatorFlag,
        })
      }
      footer={<Footer onClose={onClose} busy={m.isPending} label={t('common.create')} />}
    >
      <Field id="ncs-subject" label={t('crm.cases.field.subject')}>
        <input
          id="ncs-subject"
          className="input"
          required
          value={f.subject}
          onChange={(e) => setF({ ...f, subject: e.target.value })}
        />
      </Field>
      <Field id="ncs-desc" label={t('crm.cases.field.description')}>
        <textarea
          id="ncs-desc"
          className="textarea"
          dir="auto"
          value={f.description}
          onChange={(e) => setF({ ...f, description: e.target.value })}
        />
      </Field>
      <div className="form-grid">
        <Field id="ncs-priority" label={t('crm.cases.field.priority')}>
          <Select
            id="ncs-priority"
            value={f.priority}
            onChange={(v) => setF({ ...f, priority: v })}
            options={(['low', 'normal', 'high', 'urgent'] as const).map((p) => ({
              value: p,
              label: t(`crm.priority.${p}`),
            }))}
          />
        </Field>
        <Field id="ncs-category" label={t('crm.cases.field.category')}>
          <input
            id="ncs-category"
            className="input"
            value={f.category}
            onChange={(e) => setF({ ...f, category: e.target.value })}
          />
        </Field>
        <Field id="ncs-person" label={t('crm.cases.field.customer')}>
          <PersonPicker id="ncs-person" value={f.personId} onChange={(v) => setF({ ...f, personId: v })} />
        </Field>
        <Field id="ncs-assignee" label={t('crm.cases.field.assignee')}>
          <UserSelect
            id="ncs-assignee"
            value={f.assigneeUserId}
            onChange={(v) => setF({ ...f, assigneeUserId: v })}
          />
        </Field>
      </div>
      <label className="switch">
        <span>{t('crm.cases.field.regulator')}</span>
        <input
          type="checkbox"
          checked={f.regulatorFlag}
          onChange={(e) => setF({ ...f, regulatorFlag: e.target.checked })}
        />
      </label>
      {m.isError && <ErrorState error={m.error} />}
    </Dialog>
  );
}

export function NewTaskDialog({
  open,
  onClose,
  relatedType,
  relatedId,
}: {
  open: boolean;
  onClose: () => void;
  relatedType?: string;
  relatedId?: string;
}) {
  const { t } = useTranslation();
  const { session } = useAuth();
  const fmt = useFormat();
  const [f, setF] = useState({
    title: '',
    dueAt: '',
    priority: 'normal',
    assigneeUserId: session?.user.id ?? '',
  });
  const m = useCrmMutation(post('/v1/tasks'), () => onClose());
  return (
    <Dialog
      open={open}
      title={t('crm.tasks.new')}
      onClose={onClose}
      testId="new-task"
      onSubmit={() =>
        m.mutate({
          title: f.title,
          dueAt: f.dueAt ? zonedWallTimeToIso(f.dueAt, fmt.timezone) : null,
          priority: f.priority,
          assigneeUserId: nul(f.assigneeUserId) ?? null,
          relatedType,
          relatedId,
        })
      }
      footer={<Footer onClose={onClose} busy={m.isPending} label={t('common.create')} />}
    >
      <Field id="nt-title" label={t('crm.tasks.field.title')}>
        <input
          id="nt-title"
          className="input"
          required
          value={f.title}
          onChange={(e) => setF({ ...f, title: e.target.value })}
        />
      </Field>
      <div className="form-grid">
        <Field id="nt-due" label={t('crm.tasks.field.dueAt')} hint={fmt.zoneCaption}>
          <DateTimeField id="nt-due" value={f.dueAt} onChange={(v) => setF({ ...f, dueAt: v })} />
        </Field>
        <Field id="nt-priority" label={t('crm.tasks.field.priority')}>
          <Select
            id="nt-priority"
            value={f.priority}
            onChange={(v) => setF({ ...f, priority: v })}
            options={(['low', 'normal', 'high', 'urgent'] as const).map((p) => ({
              value: p,
              label: t(`crm.priority.${p}`),
            }))}
          />
        </Field>
        <Field id="nt-assignee" label={t('crm.tasks.field.assignee')}>
          <UserSelect
            id="nt-assignee"
            value={f.assigneeUserId}
            onChange={(v) => setF({ ...f, assigneeUserId: v })}
          />
        </Field>
      </div>
      {m.isError && <ErrorState error={m.error} />}
    </Dialog>
  );
}
